import { useEffect, useState, forwardRef } from 'react'
import { API_URL } from '../config'

interface AvatarProps {
  url?: string | null
  firstName?: string | null
  lastName?: string | null
  className?: string | null
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

// Generate a color from light blue to deep red based on two characters
const getGradientColor = (chars: string): string => {
  if (!chars || chars.length === 0) return '#3498db' // Default blue

  // Use the first two characters, or repeat the first one if only one exists
  const twoChars = chars.length >= 2 ? chars.substring(0, 2) : chars[0] + chars[0]

  // Convert characters to lowercase for consistency
  const lowerChars = twoChars.toLowerCase()

  // Calculate a value between 0 and 1 based on position in alphabet (a-z)
  // 'aa' = 0, 'zz' = 1
  // Note: ASCII 'a' = 97, 'z' = 122, so the range is actually 0-25
  const normalizeChar = (c: string) => {
    const code = c.charCodeAt(0)
    if (code < 97 || code > 122) return 0.5
    return (code - 97) / 25
  }

  let char1Value = normalizeChar(lowerChars[0])
  let char2Value = normalizeChar(lowerChars[1])

  // Special case for 'Z' - force it to be at the very end of the spectrum
  if (lowerChars[0] === 'z') {
    char1Value = 1.0
  }

  // Ensure names starting with 'z' are at the high end of the spectrum
  // Weight first letter more heavily (90/10 instead of 80/20)
  const combinedValue = char1Value * 0.9 + char2Value * 0.1

  // Rainbow gradient with modified distribution
  // Push red further to ensure 'z' names get it
  const colors = [
    { pct: 0.0, color: { r: 86, g: 153, b: 204 } }, // Softer blue (#569ccc)
    { pct: 0.2, color: { r: 75, g: 175, b: 160 } }, // Muted teal (#4bafa0)
    { pct: 0.4, color: { r: 96, g: 184, b: 113 } }, // Softer green (#60b871)
    { pct: 0.6, color: { r: 212, g: 188, b: 75 } }, // Muted yellow (#d4bc4b)
    { pct: 0.75, color: { r: 194, g: 138, b: 86 } }, // Muted orange (#c28a56)
    { pct: 0.9, color: { r: 188, g: 96, b: 87 } }, // Muted red (#bc6057)
    { pct: 1.0, color: { r: 128, g: 0, b: 0 } }, // Deep blood red (#800000)
  ]

  // Find the two colors to interpolate between
  let lower, upper
  for (let i = 0; i < colors.length - 1; i++) {
    if (combinedValue >= colors[i].pct && combinedValue <= colors[i + 1].pct) {
      lower = colors[i]
      upper = colors[i + 1]
      break
    }
  }

  // If we're at the end of the range or out of range
  if (!lower || !upper) {
    if (combinedValue >= 1.0) {
      // Guarantee deep red for highest values (including 'z')
      return `rgb(${colors[colors.length - 1].color.r}, ${colors[colors.length - 1].color.g}, ${colors[colors.length - 1].color.b})`
    }
    lower = colors[colors.length - 2]
    upper = colors[colors.length - 1]
  }

  // Calculate the interpolation factor between the two colors
  const range = upper.pct - lower.pct
  const valuePct = range === 0 ? 0 : (combinedValue - lower.pct) / range

  // Interpolate RGB values
  const r = Math.round(lower.color.r + valuePct * (upper.color.r - lower.color.r))
  const g = Math.round(lower.color.g + valuePct * (upper.color.g - lower.color.g))
  const b = Math.round(lower.color.b + valuePct * (upper.color.b - lower.color.b))

  return `rgb(${r}, ${g}, ${b})`
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ url, firstName, lastName, className, onClick }, ref) => {
    const [initials, setInitials] = useState('')
    const [bgColor, setBgColor] = useState('')
    const [fullUrl, setFullUrl] = useState('')
    const [localCssClassName, setLocalCssClassName] = useState('')
    const [imageError, setImageError] = useState(false)

    useEffect(() => {
      setImageError(false)

      if (url?.startsWith('http')) {
        setFullUrl(url)
      } else if (url) {
        setFullUrl(`${API_URL}${url}`)
      } else {
        setFullUrl('')
      }

      if (firstName && lastName) {
        const accountInitials = firstName[0] + lastName[0]
        const coloration =
          lastName.length > 1
            ? lastName.substring(0, 1)
            : lastName[0] + firstName[0]

        setBgColor(getGradientColor(coloration))
        setInitials(accountInitials)
      }

      if (url?.startsWith(`${window.location.protocol}//${window.location.host}`)) {
        setLocalCssClassName('local-avatar')
      }
    }, [firstName, lastName, url])

    return (
      <div
        ref={ref}
        className={`avatar ${className ?? ''} ${localCssClassName ?? ''}`}
        onClick={onClick}
      >
        {!imageError && fullUrl ? (
          <img
            className="image"
            src={fullUrl}
            alt="avatar"
            onError={(err) => {
              console.log({ err, firstName })
              setImageError(true)
            }}
          />
        ) : (
          initials && (
            <div className="initials" style={{ backgroundColor: bgColor }}>
              {initials}
            </div>
          )
        )}
      </div>
    )
  }
)

export default Avatar
